import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import client from '../api/client';
import { PET_AGE_OPTIONS } from '../data/petAgeCategories';
import { filterBreedsForQuery } from '../data/petBreeds';
import {
  todayIsoDate,
  validatePolicyStartDate,
} from '../components/PolicyStartDateField';
import PetWhatIsThis from '../components/PetWhatIsThis';
import {
  PET_ANNUAL_LIMIT_RECOMMENDED,
  PET_DEDUCTIBLE_MAX,
  PET_QUOTE_DEFINITIONS,
  parseAnnualLimitInput,
  parseDeductibleInput,
  type PetAnnualLimitPayload,
} from '../data/petQuotePreferences';
import { trackFunnelStep, trackGenerateLead } from '../utils/analytics';
import {
  clearLeadSession,
  readLeadIdFromSession,
  wasLeadConversionFired,
} from '../utils/leadSession';
import { isTexasZipCode } from '../utils/texasZipCode';
import './PetInsuranceFormPage.css';

type PetInsuranceFormPageProps = {
  /** Ad landing page — skip /qualify and collect zip on this form. */
  directLanding?: boolean;
};

export type PetInsuranceRequestPayload = {
  petName: string;
  species: 'dog' | 'cat';
  breed: string;
  ageCategory: string;
  weightLbs: number;
  spayedNeutered: boolean;
  zipCode: string;
  policyStartDate: 'decide_later' | string;
  annualLimit: PetAnnualLimitPayload;
  reimbursementPercent: 0 | 70 | 80 | 90;
  deductibleAmount: number;
};

function PetInsuranceFormPage({ directLanding = false }: PetInsuranceFormPageProps) {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();

  const [petName, setPetName] = useState('');
  const [species, setSpecies] = useState<'dog' | 'cat'>('dog');
  const [breed, setBreed] = useState('');
  const [ageCategory, setAgeCategory] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [policyStartMode, setPolicyStartMode] = useState<'no_preference' | 'choose_date'>('no_preference');
  const [policyStartDate, setPolicyStartDate] = useState(todayIsoDate);
  const [weightLbs, setWeightLbs] = useState('');
  const [spayedNeutered, setSpayedNeutered] = useState<boolean | null>(null);
  const [annualLimitInput, setAnnualLimitInput] = useState('');
  const [deductibleInput, setDeductibleInput] = useState('');
  const [reimbursementPercent, setReimbursementPercent] =
    useState<PetInsuranceRequestPayload['reimbursementPercent']>(0);
  const [quotePrefsMode, setQuotePrefsMode] = useState<'skip' | 'customize'>('skip');
  const [quotePrefsTouched, setQuotePrefsTouched] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [nameTouched, setNameTouched] = useState(false);

  const [breedMenuOpen, setBreedMenuOpen] = useState(false);
  const [breedHighlight, setBreedHighlight] = useState(0);
  const breedWrapRef = useRef<HTMLDivElement>(null);
  const breedInputRef = useRef<HTMLInputElement>(null);

  const breedSuggestions = useMemo(() => filterBreedsForQuery(species, breed), [species, breed]);

  useEffect(() => {
    if (!directLanding) {
      const type = sessionStorage.getItem('insuranceType');
      const zip = sessionStorage.getItem('zipCode') || '';
      if (type !== 'pet' || zip.length !== 5) return;
    }

    trackFunnelStep('form_view', {
      entry_point: directLanding ? 'ad_landing' : 'qualify',
    });
  }, [directLanding]);

  useEffect(() => {
    setBreedHighlight(0);
  }, [breedSuggestions]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (breedWrapRef.current && !breedWrapRef.current.contains(e.target as Node)) {
        setBreedMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  useEffect(() => {
    const zip = sessionStorage.getItem('zipCode') || '';

    if (directLanding) {
      sessionStorage.setItem('insuranceType', 'pet');
      if (zip.length === 5) setZipCode(zip);
      return;
    }

    const type = sessionStorage.getItem('insuranceType');
    if (type !== 'pet' || zip.length !== 5) {
      navigate('/qualify');
      return;
    }
    setZipCode(zip);
  }, [directLanding, navigate]);

  const selectSpecies = (s: 'dog' | 'cat') => {
    setSpecies(s);
    setBreed('');
    setBreedMenuOpen(false);
  };

  const pickBreed = (value: string) => {
    setBreed(value);
    setBreedMenuOpen(false);
    breedInputRef.current?.blur();
  };

  const onBreedKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!breedMenuOpen && (e.key === 'ArrowDown' || e.key === 'ArrowUp') && breed.trim()) {
      setBreedMenuOpen(true);
    }
    if (!breedSuggestions.length) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setBreedMenuOpen(true);
      setBreedHighlight((h) => Math.min(h + 1, breedSuggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setBreedMenuOpen(true);
      setBreedHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter' && breedMenuOpen && breedSuggestions[breedHighlight]) {
      e.preventDefault();
      pickBreed(breedSuggestions[breedHighlight]);
    } else if (e.key === 'Escape') {
      setBreedMenuOpen(false);
    }
  };

  const resolvedAnnualLimit = parseAnnualLimitInput(annualLimitInput);
  const resolvedDeductible = parseDeductibleInput(deductibleInput);

  const chooseSkipQuotePrefs = () => {
    setQuotePrefsMode('skip');
    setAnnualLimitInput('');
    setDeductibleInput('');
    setReimbursementPercent(0);
    setQuotePrefsTouched(false);
  };

  const chooseCustomizeQuotePrefs = () => {
    setQuotePrefsMode('customize');
  };

  const buildPayload = (): PetInsuranceRequestPayload => ({
    petName: petName.trim(),
    species,
    breed: breed.trim(),
    ageCategory,
    weightLbs: parseFloat(weightLbs),
    spayedNeutered: spayedNeutered === true,
    zipCode,
    policyStartDate: policyStartMode === 'no_preference' ? 'decide_later' : policyStartDate,
    annualLimit: quotePrefsMode === 'skip' ? 'decide_later' : resolvedAnnualLimit.value,
    reimbursementPercent: quotePrefsMode === 'skip' ? 0 : reimbursementPercent,
    deductibleAmount: quotePrefsMode === 'skip' ? 0 : resolvedDeductible.value,
  });

  const validateClient = (): string | null => {
    if (!petName.trim()) return "Please enter your pet's name.";
    if (!breed.trim()) return 'Please enter or choose a breed.';
    if (!ageCategory) return 'Please select your pet’s age range.';
    const w = parseFloat(weightLbs);
    if (!weightLbs.trim() || Number.isNaN(w) || w <= 0) return 'Please enter your pet’s weight in pounds.';
    if (w > 500) return 'Please enter a realistic weight in pounds.';
    if (spayedNeutered === null) return 'Please tell us if your pet is spayed or neutered.';
    if (!/^\d{5}$/.test(zipCode)) return 'Zip code must be 5 digits.';
    if (directLanding && !isTexasZipCode(zipCode)) {
      return 'We currently offer pet insurance in Texas only.';
    }
    if (policyStartMode === 'choose_date') {
      const policyStartErr = validatePolicyStartDate(policyStartDate);
      if (policyStartErr) return policyStartErr;
    }
    if (quotePrefsMode === 'customize') {
      if (resolvedAnnualLimit.error) return resolvedAnnualLimit.error;
      if (resolvedDeductible.error) return resolvedDeductible.error;
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setNameTouched(true);
    setQuotePrefsTouched(true);
    const msg = validateClient();
    if (msg) {
      setError(msg);
      return;
    }

    const payload = {
      ...buildPayload(),
      ...(readLeadIdFromSession() != null ? { leadId: readLeadIdFromSession() } : {}),
    };

    trackFunnelStep('form_submit', {
      logged_in: isAuthenticated ? 'yes' : 'no',
      entry_point: directLanding ? 'ad_landing' : 'qualify',
    });

    if (!isAuthenticated) {
      trackFunnelStep('signup_redirect');
      sessionStorage.setItem('petInsuranceFormData', JSON.stringify(payload));
      sessionStorage.setItem('insuranceType', 'pet');
      sessionStorage.setItem('zipCode', zipCode);
      sessionStorage.setItem('redirectAfterSignup', '/insurance/requests');
      navigate('/register');
      return;
    }

    setLoading(true);
    try {
      await client.post('/insurance/pet-request', payload);
      if (!wasLeadConversionFired()) {
        await trackGenerateLead('pet');
      }
      sessionStorage.removeItem('petInsuranceFormData');
      sessionStorage.removeItem('insuranceType');
      sessionStorage.removeItem('zipCode');
      clearLeadSession();
      navigate('/insurance/requests');
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { error?: string; message?: string } } };
      setError(
        ax.response?.data?.error || ax.response?.data?.message || 'Failed to submit your pet insurance request'
      );
    } finally {
      setLoading(false);
    }
  };

  const showNameError = nameTouched && !petName.trim();
  const showBreedDropdown = breedMenuOpen && breed.trim().length > 0 && breedSuggestions.length > 0;

  return (
    <div
      className="container pet-form-page"
      style={{ maxWidth: '700px', margin: '2rem auto' }}
    >
      <div className="card pet-form-card">
        <div className="card-header">
          {directLanding ? 'Pet insurance quote for Texans' : 'Pet insurance quote'}
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="petName">Pet&apos;s name *</label>
            <input
              id="petName"
              type="text"
              value={petName}
              onChange={(e) => setPetName(e.target.value)}
              onBlur={() => setNameTouched(true)}
              placeholder="Pet's name"
              autoComplete="off"
              className={showNameError ? 'input-error' : undefined}
              required
            />
            {showNameError && (
              <div className="pet-field-hint" role="alert">
                Please enter your pet&apos;s name.
              </div>
            )}
          </div>

          <div className="form-group">
            <span id="species-label" className="pet-label-text">
              Pet type *
            </span>
            <div className="pet-type-row" role="group" aria-labelledby="species-label">
              <button
                type="button"
                className={`pet-type-choice ${species === 'dog' ? 'pet-type-choice--selected' : ''}`}
                onClick={() => selectSpecies('dog')}
              >
                Dog
              </button>
              <button
                type="button"
                className={`pet-type-choice ${species === 'cat' ? 'pet-type-choice--selected' : ''}`}
                onClick={() => selectSpecies('cat')}
              >
                Cat
              </button>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="ageCategory">Age *</label>
            <select
              id="ageCategory"
              value={ageCategory}
              onChange={(e) => setAgeCategory(e.target.value)}
              required
            >
              <option value="">How old?</option>
              {PET_AGE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group pet-breed-field" ref={breedWrapRef}>
            <label htmlFor="breed">Breed *</label>
            <input
              ref={breedInputRef}
              id="breed"
              type="text"
              value={breed}
              onChange={(e) => {
                setBreed(e.target.value);
                setBreedMenuOpen(true);
              }}
              onFocus={() => {
                if (breed.trim()) setBreedMenuOpen(true);
              }}
              onKeyDown={onBreedKeyDown}
              placeholder="Start typing a breed…"
              autoComplete="off"
              role="combobox"
              aria-expanded={showBreedDropdown}
              aria-controls="breed-suggestions"
              aria-autocomplete="list"
              required
            />
            <p className="pet-breed-hint">You can put &quot;Unknown&quot; if you aren&apos;t sure.</p>
            {showBreedDropdown && (
              <ul
                id="breed-suggestions"
                className="pet-breed-suggestions"
                role="listbox"
                aria-label="Breed suggestions"
              >
                {breedSuggestions.map((opt, i) => (
                  <li
                    key={`${opt}-${i}`}
                    role="option"
                    aria-selected={i === breedHighlight}
                    className={`pet-breed-option ${i === breedHighlight ? 'pet-breed-option--active' : ''}`}
                    onMouseDown={(ev) => {
                      ev.preventDefault();
                      pickBreed(opt);
                    }}
                    onMouseEnter={() => setBreedHighlight(i)}
                  >
                    {opt}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="zipCode">Zip code *</label>
            <input
              id="zipCode"
              type="text"
              inputMode="numeric"
              maxLength={5}
              value={zipCode}
              onChange={(e) => setZipCode(e.target.value.replace(/\D/g, ''))}
              placeholder={directLanding ? 'Texas zip code' : '5-digit zip'}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="weightLbs">Weight (lbs) *</label>
            <input
              id="weightLbs"
              type="number"
              min={1}
              max={500}
              step={0.1}
              value={weightLbs}
              onChange={(e) => setWeightLbs(e.target.value)}
              placeholder="e.g. 45"
              required
            />
          </div>

          <div className="form-group">
            <span className="pet-label-text">Spayed or neutered? *</span>
            <div className="pet-spay-row">
              <label className="pet-spay-option">
                <input
                  type="radio"
                  name="spayed"
                  checked={spayedNeutered === true}
                  onChange={() => setSpayedNeutered(true)}
                  required
                />
                Yes
              </label>
              <label className="pet-spay-option">
                <input
                  type="radio"
                  name="spayed"
                  checked={spayedNeutered === false}
                  onChange={() => setSpayedNeutered(false)}
                />
                No
              </label>
            </div>
          </div>

          <section className="pet-policy-start-section" aria-labelledby="pet-policy-start-heading">
            <span id="pet-policy-start-heading" className="pet-label-text">
              When do you need your policy to start?{' '}
              <span className="pet-optional-label">(optional)</span>
            </span>

            <div className="pet-quote-prefs-row" role="radiogroup" aria-labelledby="pet-policy-start-heading">
              <label className="pet-spay-option">
                <input
                  type="radio"
                  name="policyStartMode"
                  checked={policyStartMode === 'no_preference'}
                  onChange={() => setPolicyStartMode('no_preference')}
                />
                Skip for now
              </label>
              <label className="pet-spay-option">
                <input
                  type="radio"
                  name="policyStartMode"
                  checked={policyStartMode === 'choose_date'}
                  onChange={() => setPolicyStartMode('choose_date')}
                />
                Choose a date
              </label>
            </div>

            {policyStartMode === 'choose_date' && (
              <div className="form-group pet-policy-start-date">
                <label htmlFor="petPolicyStartDate" className="pet-sr-only">
                  Policy start date
                </label>
                <input
                  id="petPolicyStartDate"
                  type="date"
                  min={todayIsoDate()}
                  value={policyStartDate}
                  onChange={(e) => setPolicyStartDate(e.target.value)}
                  style={{ maxWidth: '16rem' }}
                />
              </div>
            )}
          </section>

          <section className="pet-quote-prefs-section" aria-labelledby="pet-quote-prefs-heading">
            <span id="pet-quote-prefs-heading" className="pet-label-text">
              Quote preferences{' '}
              <span className="pet-optional-label">(optional)</span>
            </span>

            <div className="pet-quote-prefs-row" role="radiogroup" aria-labelledby="pet-quote-prefs-heading">
              <label className="pet-spay-option">
                <input
                  type="radio"
                  name="quotePrefsMode"
                  checked={quotePrefsMode === 'skip'}
                  onChange={chooseSkipQuotePrefs}
                />
                Skip for now — I&apos;ll decide with an agent
              </label>
              <label className="pet-spay-option">
                <input
                  type="radio"
                  name="quotePrefsMode"
                  checked={quotePrefsMode === 'customize'}
                  onChange={chooseCustomizeQuotePrefs}
                />
                Customize now
              </label>
            </div>

            {quotePrefsMode === 'customize' && (
              <div className="pet-quote-prefs-fields">
          <div className="form-group">
            <label htmlFor="annualLimit">Annual limit</label>
            <div className="pet-dollar-input-wrap">
              <span className="pet-dollar-prefix" aria-hidden="true">
                $
              </span>
              <input
                id="annualLimit"
                type="text"
                inputMode="numeric"
                value={annualLimitInput}
                onChange={(e) => setAnnualLimitInput(e.target.value.replace(/\D/g, ''))}
                onBlur={() => setQuotePrefsTouched(true)}
                placeholder={`Not sure? We recommend starting with ${PET_ANNUAL_LIMIT_RECOMMENDED.toLocaleString()}`}
                className="pet-input-placeholder-hint"
              />
            </div>
            {quotePrefsTouched && resolvedAnnualLimit.error && (
              <div className="pet-field-hint" role="alert">
                {resolvedAnnualLimit.error}
              </div>
            )}
            <PetWhatIsThis definition={PET_QUOTE_DEFINITIONS.annualLimit} alignWithDollarInput />
          </div>

          <div className="form-group">
            <label htmlFor="reimbursementPercent">Reimbursement</label>
            <div className="pet-field-control">
              <select
                id="reimbursementPercent"
                value={reimbursementPercent}
                onChange={(e) =>
                  setReimbursementPercent(Number(e.target.value) as PetInsuranceRequestPayload['reimbursementPercent'])
                }
              >
                <option value={0}>Decide later</option>
                <option value={70}>70%</option>
                <option value={80}>80%</option>
                <option value={90}>90%</option>
              </select>
              <PetWhatIsThis definition={PET_QUOTE_DEFINITIONS.reimbursement} underSelect />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="deductibleAmount">Deductible</label>
            <div className="pet-dollar-input-wrap">
              <span className="pet-dollar-prefix" aria-hidden="true">
                $
              </span>
              <input
                id="deductibleAmount"
                type="text"
                inputMode="numeric"
                value={deductibleInput}
                onChange={(e) => setDeductibleInput(e.target.value.replace(/\D/g, ''))}
                onBlur={() => setQuotePrefsTouched(true)}
                placeholder={`e.g. 250 (up to ${PET_DEDUCTIBLE_MAX.toLocaleString()})`}
                className="pet-input-placeholder-hint"
                maxLength={4}
              />
            </div>
            {quotePrefsTouched && resolvedDeductible.error && (
              <div className="pet-field-hint" role="alert">
                {resolvedDeductible.error}
              </div>
            )}
            <PetWhatIsThis definition={PET_QUOTE_DEFINITIONS.deductible} alignWithDollarInput />
          </div>
              </div>
            )}
          </section>

          <button type="submit" className="btn btn-primary" style={{ width: '100%', marginTop: '0.5rem' }} disabled={loading}>
            {loading ? 'Submitting…' : isAuthenticated ? 'Submit quote request' : 'Continue to create account'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default PetInsuranceFormPage;
